import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SEO } from '../components/shared/SEO';
import { FileText, AlertTriangle, Copyright, ChevronRight, ArrowLeft, Scale } from 'lucide-react';

const Terms: React.FC = () => {
  const lastUpdated = '2026年3月31日';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50 dark:from-[#0b1220] dark:via-slate-900 dark:to-slate-800 py-8 sm:py-12 px-3 sm:px-4">
      <SEO 
        title="使用条款"
        description="使用 lingdux.space 前请阅读本使用条款和条件。"
      />
      <div className="max-w-3xl mx-auto space-y-6 sm:space-y-8">
        
        {/* Header */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 shadow-[0_24px_70px_-44px_rgba(15,23,42,0.25)] p-6 sm:p-8 md:p-10"
        >
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            返回首页
          </Link>
          
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-2xl bg-amber-100 dark:bg-amber-900/30">
              <Scale className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">使用条款</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">最后更新：{lastUpdated}</p>
            </div>
          </div>
          
          <p className="text-slate-600 dark:text-slate-300 leading-7">
            请在使用本网站前仔细阅读本使用条款。访问和使用本网站的任何内容，即表示您同意受这些条款的约束。
          </p>
        </motion.section>

        {/* Content Sections */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 shadow-[0_24px_70px_-44px_rgba(15,23,42,0.25)] p-6 sm:p-8 md:p-10 space-y-6"
        >
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-500" />
            网站内容
          </h2>
          <div className="text-slate-600 dark:text-slate-300 space-y-4 leading-7">
            <p>本网站的所有内容，包括但不限于：</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>文章、博客帖子和其他文字内容</li>
              <li>图片、图标和其他视觉元素</li>
              <li>网站设计和布局</li>
              <li>代码和软件</li>
            </ul>
            <p>除非另有说明，这些内容均为本人原创或经合法授权使用。</p>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 shadow-[0_24px_70px_-44px_rgba(15,23,42,0.25)] p-6 sm:p-8 md:p-10 space-y-6"
        >
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Copyright className="w-5 h-5 text-emerald-500" />
            知识产权
          </h2>
          <div className="text-slate-600 dark:text-slate-300 space-y-4 leading-7">
            <p>除非特别声明，本网站上您可见的所有内容均受知识产权保护。</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>原创内容</strong>：未经本人明确许可，禁止转载、复制或分发本人创作的文章和内容</li>
              <li><strong>代码</strong>：技术文章中的代码示例可在注明出处的前提下自由使用</li>
              <li><strong>第三方内容</strong>：第三方品牌、logo 和内容版权归各自所有者所有</li>
            </ul>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-4">
              如需转载或引用，请通过联系页面与我取得联系。
            </p>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 shadow-[0_24px_70px_-44px_rgba(15,23,42,0.25)] p-6 sm:p-8 md:p-10 space-y-6"
        >
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            免责声明
          </h2>
          <div className="text-slate-600 dark:text-slate-300 space-y-4 leading-7">
            <p>本网站的内容按"原样"提供，不提供任何明示或暗示的保证：</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>内容准确性：我不保证网站上所有信息的完全准确性</li>
              <li>可用性：我不保证网站始终可访问或无错误</li>
              <li>适用性：网站内容不构成任何专业建议（技术、法律、医疗等）</li>
            </ul>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-4">
              因使用本网站内容导致的任何损失，我方不承担责任。
            </p>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 shadow-[0_24px_70px_-44px_rgba(15,23,42,0.25)] p-6 sm:p-8 md:p-10 space-y-6"
        >
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-violet-500" />
            用户行为
          </h2>
          <div className="text-slate-600 dark:text-slate-300 space-y-4 leading-7">
            <p>使用本网站时，请遵守以下规定：</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>不得以任何方式破坏、干扰或非法访问本网站</li>
              <li>不得传播任何恶意代码、病毒或有害内容</li>
              <li>评论和留言应保持文明、礼貌</li>
              <li>不得利用本网站从事任何违法活动</li>
            </ul>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 shadow-[0_24px_70px_-44px_rgba(15,23,42,0.25)] p-6 sm:p-8 md:p-10 space-y-6"
        >
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-rose-500" />
            外部链接
          </h2>
          <div className="text-slate-600 dark:text-slate-300 space-y-4 leading-7">
            <p>
              本网站可能包含指向第三方网站的链接。这些链接仅为方便用户而提供，
              我不对这些第三方网站的内容、隐私政策或服务质量负责。
            </p>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl sm:rounded-[30px] border border-white/80 dark:border-slate-800/80 shadow-[0_24px_70px_-44px_rgba(15,23,42,0.25)] p-6 sm:p-8 md:p-10 space-y-6"
        >
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Scale className="w-5 h-5 text-sky-500" />
            条款修改
          </h2>
          <div className="text-slate-600 dark:text-slate-300 space-y-4 leading-7">
            <p>
              我保留随时修改本使用条款的权利。修改后的条款将在本页面发布，
              继续使用本网站即表示您接受修改后的条款。
            </p>
          </div>
        </motion.section>

        {/* Contact */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl sm:rounded-[30px] shadow-[0_28px_70px_-44px_rgba(245,158,11,0.45)] p-6 sm:p-8 md:p-10"
        >
          <h2 className="text-xl font-bold text-white mb-3">联系我们</h2>
          <p className="text-white/80 leading-7 mb-4">
            如果您对本使用条款有任何疑问，请通过以下方式联系我们：
          </p>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 rounded-xl bg-white/15 px-5 py-2.5 text-white font-medium hover:bg-white/25 transition-colors"
          >
            前往联系页面
            <ChevronRight className="w-4 h-4" />
          </Link>
        </motion.section>

      </div>
    </div>
  );
};

export default Terms;
